"use client"

import { useState } from "react"
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query"
import { toast } from "sonner"
import { cn } from "@/lib/utils"
import { Input } from "@/components/ui/input"
import { getTradingOptions, updateTradingOptions } from "@/lib/api/trading"
import { FieldRow, SaveButton, CardSkeleton, INPUT_CLASS } from "@/components/trading/shared"
import type { TradingStrategyOption } from "@/types/trading"

// TimeOnly? 필드는 System.Text.Json 라운드트립 포맷("HH:mm:ss.fffffff")을 요구하므로
// <input type="time">의 "HH:mm" 값과 상호 변환한다.
function toTimeInputValue(t: string | null | undefined): string {
  if (!t) return ""
  return t.slice(0, 5)
}

function toApiTime(hhmm: string): string | null {
  if (!hhmm) return null
  return `${hhmm}:00.0000000`
}

const DEFAULTS = {
  volumeRatioThreshold: 15,
  priceChangeThreshold: 3,
  maxSplitBuyCount: 3,
  priceBandPct: 1,
  additionalBuyDropPct: -20,
  targetProfitPct: 10,
}

function findOption(options: TradingStrategyOption[], type: TradingStrategyOption["conditionType"]) {
  return options.find((o) => o.conditionType === type)
}

function SectionLabel({ children }: { children: React.ReactNode }) {
  return <h3 className="text-xs font-semibold tracking-wide text-zinc-500 uppercase">{children}</h3>
}

function StrategyParamsForm({ options }: { options: TradingStrategyOption[] }) {
  const queryClient = useQueryClient()
  const yudong = findOption(options, "유동주식수")
  const ilbong = findOption(options, "일봉주봉")
  const common = findOption(options, "공통")

  const [volumeRatioThreshold, setVolumeRatioThreshold] = useState(
    String(yudong?.volumeRatioThreshold ?? DEFAULTS.volumeRatioThreshold)
  )
  const [priceChangeThreshold, setPriceChangeThreshold] = useState(
    String(yudong?.priceChangeThreshold ?? DEFAULTS.priceChangeThreshold)
  )
  const [maxSplitBuyCount, setMaxSplitBuyCount] = useState(
    String(yudong?.maxSplitBuyCount ?? DEFAULTS.maxSplitBuyCount)
  )
  const [buyAfterTime, setBuyAfterTime] = useState(toTimeInputValue(yudong?.buyAfterTime))
  const [priceBandPct, setPriceBandPct] = useState(String(ilbong?.priceBandPct ?? DEFAULTS.priceBandPct))
  const [additionalBuyDropPct, setAdditionalBuyDropPct] = useState(
    String(ilbong?.additionalBuyDropPct ?? DEFAULTS.additionalBuyDropPct)
  )
  const [targetProfitPct, setTargetProfitPct] = useState(
    String(common?.targetProfitPct ?? DEFAULTS.targetProfitPct)
  )

  const saveMutation = useMutation({
    mutationFn: async () => {
      const payload: TradingStrategyOption[] = [
        {
          conditionType: "유동주식수",
          volumeRatioThreshold: Number(volumeRatioThreshold),
          priceChangeThreshold: Number(priceChangeThreshold),
          maxSplitBuyCount: Number(maxSplitBuyCount),
          priceBandPct: null,
          additionalBuyDropPct: null,
          targetProfitPct: null,
          buyAfterTime: toApiTime(buyAfterTime),
        },
        {
          conditionType: "일봉주봉",
          volumeRatioThreshold: null,
          priceChangeThreshold: null,
          maxSplitBuyCount: null,
          priceBandPct: Number(priceBandPct),
          additionalBuyDropPct: Number(additionalBuyDropPct),
          targetProfitPct: null,
          buyAfterTime: ilbong?.buyAfterTime ?? null,
        },
        {
          conditionType: "공통",
          volumeRatioThreshold: null,
          priceChangeThreshold: null,
          maxSplitBuyCount: null,
          priceBandPct: null,
          additionalBuyDropPct: null,
          targetProfitPct: Number(targetProfitPct),
          buyAfterTime: common?.buyAfterTime ?? null,
        },
      ]
      await updateTradingOptions(payload)
    },
    onSuccess: () => {
      toast.success("전략 파라미터가 저장되었습니다")
      queryClient.invalidateQueries({ queryKey: ["trading", "options"] })
    },
    onError: () => toast.error("저장에 실패했습니다"),
  })

  const canSave = [
    volumeRatioThreshold,
    priceChangeThreshold,
    maxSplitBuyCount,
    priceBandPct,
    additionalBuyDropPct,
    targetProfitPct,
  ].every((v) => v.trim() !== "" && !Number.isNaN(Number(v)))

  return (
    <div className="flex flex-col gap-5 rounded-lg border border-zinc-800 bg-zinc-900 p-4">
      <h2 className="text-base font-semibold text-zinc-100">전략 파라미터</h2>

      <div className="flex flex-col gap-3">
        <SectionLabel>유동주식수</SectionLabel>
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-3">
          <FieldRow label="거래량 비율 임계값" unit="%">
            <Input
              type="number"
              value={volumeRatioThreshold}
              onChange={(e) => setVolumeRatioThreshold(e.target.value)}
              className={INPUT_CLASS}
            />
          </FieldRow>
          <FieldRow label="등락률 임계값" unit="%">
            <Input
              type="number"
              value={priceChangeThreshold}
              onChange={(e) => setPriceChangeThreshold(e.target.value)}
              className={INPUT_CLASS}
            />
          </FieldRow>
          <FieldRow label="최대 분할매수 횟수" unit="회">
            <Input
              type="number"
              min={1}
              step={1}
              value={maxSplitBuyCount}
              onChange={(e) => setMaxSplitBuyCount(e.target.value)}
              className={INPUT_CLASS}
            />
          </FieldRow>
          <FieldRow label="매수 가능 시작 시각">
            <Input
              type="time"
              value={buyAfterTime}
              onChange={(e) => setBuyAfterTime(e.target.value)}
              className={cn(INPUT_CLASS, "max-w-36")}
            />
            <span className="text-xs text-zinc-500">비워두면 시간 제한 없이 매수합니다.</span>
          </FieldRow>
        </div>
      </div>

      <div className="flex flex-col gap-3 border-t border-zinc-800 pt-4">
        <SectionLabel>일봉주봉</SectionLabel>
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
          <FieldRow label="매수 밴드" unit="%">
            <Input
              type="number"
              value={priceBandPct}
              onChange={(e) => setPriceBandPct(e.target.value)}
              className={INPUT_CLASS}
            />
          </FieldRow>
          <FieldRow label="추가매수 하락률" unit="%">
            <Input
              type="number"
              value={additionalBuyDropPct}
              onChange={(e) => setAdditionalBuyDropPct(e.target.value)}
              className={INPUT_CLASS}
            />
          </FieldRow>
        </div>
      </div>

      <div className="flex flex-col gap-3 border-t border-zinc-800 pt-4">
        <SectionLabel>공통</SectionLabel>
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-3">
          <FieldRow label="목표수익률" unit="%">
            <Input
              type="number"
              value={targetProfitPct}
              onChange={(e) => setTargetProfitPct(e.target.value)}
              className={INPUT_CLASS}
            />
          </FieldRow>
        </div>
      </div>

      <SaveButton pending={saveMutation.isPending} disabled={!canSave} onClick={() => saveMutation.mutate()} />
    </div>
  )
}

export function StrategyParamsTab() {
  const { data: options, isLoading } = useQuery({
    queryKey: ["trading", "options"],
    queryFn: getTradingOptions,
  })

  if (isLoading || options === undefined) {
    return <CardSkeleton rows={6} title="전략 파라미터" />
  }
  return <StrategyParamsForm options={options} />
}
